import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Autosuggest from 'react-autosuggest';
import './LitterProfile.css';

const LitterProfile = ({ playerId }) => {
  const { litterId } = useParams();
  const [litter, setLitter] = useState(null);
  const [dogLookup, setDogLookup] = useState({});
  const [kennelLookup, setKennelLookup] = useState({});
  const [message, setMessage] = useState('');
  const [artUrl, setArtUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [editingPuppyIdx, setEditingPuppyIdx] = useState(null);
  const [puppyEdits, setPuppyEdits] = useState({});
  const [transferIdx, setTransferIdx] = useState(null);
  const [newOwnerUsername, setNewOwnerUsername] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [healthTestVisibility, setHealthTestVisibility] = useState({});
  const [puppyImageUrls, setPuppyImageUrls] = useState({});
  const [sireImageUrl, setSireImageUrl] = useState('https://via.placeholder.com/150?text=Sire');
  const [damImageUrl, setDamImageUrl] = useState('https://via.placeholder.com/150?text=Dam');
  const navigate = useNavigate();

  const PLACEHOLDER_URL = 'https://via.placeholder.com/150?text=Puppy';

  useEffect(() => {
    const fetchLitter = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/litter/${litterId}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setLitter(data);

        const dogIds = new Set([data.sireId, data.damId]);
        data.puppies.forEach(puppy => {
          if (puppy.dogId?._id) dogIds.add(puppy.dogId._id.toString());
        });

        const dogData = await Promise.all(
          [...dogIds].map(async dogId => {
            const res = await fetch(`${API_BASE_URL}/public-dog/${dogId}`);
            if (!res.ok) return null;
            const dog = await res.json();
            return [dogId, dog];
          })
        );

        const kennelIds = new Set([data.bredBy]);
        if (data.coBreederKennelId) kennelIds.add(data.coBreederKennelId);
        const kennelData = await Promise.all(
          [...kennelIds].map(async kennelId => {
            const res = await fetch(`${API_BASE_URL}/public-kennel/${kennelId}`);
            if (!res.ok) return null;
            const kennel = await res.json();
            return [kennelId, kennel.kennelName];
          })
        );

        setDogLookup(Object.fromEntries(dogData.filter(d => d)));
        setKennelLookup(Object.fromEntries(kennelData.filter(k => k)));
        setArtUrl(data.artUrl || '');

        const initialImageUrls = {};
        data.puppies.forEach((puppy, idx) => {
          initialImageUrls[idx] = puppy.artUrl || PLACEHOLDER_URL;
        });
        setPuppyImageUrls(initialImageUrls);

        setIsLoading(false);
      } catch (error) {
        setMessage('Error fetching litter: ' + error.message);
        setIsLoading(false);
      }
    };

    fetchLitter();
  }, [litterId]);

  useEffect(() => {
    if (litter && dogLookup[litter.sireId]?.artUrl) {
      console.log('Sire artUrl:', dogLookup[litter.sireId].artUrl);
      setSireImageUrl(dogLookup[litter.sireId].artUrl);
    } else {
      console.log('Sire artUrl not found, using placeholder');
      setSireImageUrl('https://via.placeholder.com/150?text=Sire');
    }

    if (litter && dogLookup[litter.damId]?.artUrl) {
      console.log('Dam artUrl:', dogLookup[litter.damId].artUrl);
      setDamImageUrl(dogLookup[litter.damId].artUrl);
    } else {
      console.log('Dam artUrl not found, using placeholder');
      setDamImageUrl('https://via.placeholder.com/150?text=Dam');
    }
  }, [litter, dogLookup]);

  const addArtUrl = async () => {
    if (!artUrl) return;
    try {
      const res = await fetch(`${API_BASE_URL}/litter-art/${litterId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ artUrl })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLitter(data.litter);
      setArtUrl('');
      setMessage(data.message);
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const clearLitterArt = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/clear-litter-art`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, litterId })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLitter({ ...litter, artUrl: null });
      setArtUrl('');
      setMessage(data.message);
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const startEditingPuppy = (idx) => {
    setEditingPuppyIdx(idx);
    setPuppyEdits({
      name: litter.puppies[idx].name || '',
      registeredName: litter.puppies[idx].registeredName || '',
      personality: litter.puppies[idx].personality || '',
      phenotype: litter.puppies[idx].phenotype || '',
      genotype: litter.puppies[idx].genotype || '',
      color: litter.puppies[idx].color || '',
      artUrl: litter.puppies[idx].artUrl || ''
    });
  };

  const savePuppyEdits = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/update-puppy/${litterId}/${editingPuppyIdx}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, updates: puppyEdits })
      });
      if (!res.ok) throw new Error(await res.text());
      const updatedLitter = await res.json();
      setLitter(updatedLitter);
      
      setPuppyImageUrls(prev => ({
        ...prev,
        [editingPuppyIdx]: puppyEdits.artUrl || PLACEHOLDER_URL
      }));

      setEditingPuppyIdx(null);
      setPuppyEdits({});
      setMessage('Puppy updated successfully');
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const cancelEditingPuppy = () => {
    setEditingPuppyIdx(null);
    setPuppyEdits({});
  };

  const markPuppyKeeper = async (idx) => {
    try {
      const res = await fetch(`${API_BASE_URL}/mark-puppy-keeper/${litterId}/${idx}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLitter(data.litter);
      setMessage(data.message);
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const unmarkPuppyKeeper = async (idx) => {
    try {
      const res = await fetch(`${API_BASE_URL}/unmark-puppy-keeper/${litterId}/${idx}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLitter(data.litter);
      setMessage(data.message);
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const fetchSuggestions = async (value) => {
    if (!value) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/search-players/${encodeURIComponent(value)}`);
      if (!res.ok) throw new Error(await res.text());
      setSuggestions(await res.json());
    } catch (error) {
      console.error('Fetch suggestions error:', error.message);
    }
  };

  const transferPuppy = async (idx) => {
    if (!newOwnerUsername) {
      setMessage('Please select a valid player to transfer the puppy to.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/transfer-puppy/${litterId}/${idx}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, newOwnerUsername })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLitter(data.litter);
      setMessage(data.message || 'Puppy transferred successfully');
      setTransferIdx(null);
      setNewOwnerUsername('');
      setSuggestions([]);
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const toggleTransfer = (idx) => {
    if (transferIdx === idx) {
      setTransferIdx(null);
      setNewOwnerUsername('');
      setSuggestions([]);
    } else {
      setTransferIdx(idx);
    }
  };

  const toggleHealthTests = (idx) => {
    setHealthTestVisibility(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  const handleParentImageError = (type) => {
    if (type === 'sire') {
      console.log('Sire image failed to load, using placeholder');
      setSireImageUrl('https://via.placeholder.com/150?text=Sire');
    } else {
      console.log('Dam image failed to load, using placeholder');
      setDamImageUrl('https://via.placeholder.com/150?text=Dam');
    }
  };

  const handleImageError = (idx) => {
    console.log(`Puppy ${idx + 1} image failed to load, using placeholder`);
    setPuppyImageUrls(prev => ({
      ...prev,
      [idx]: PLACEHOLDER_URL
    }));
  };

  const renderHealthTestsTable = (tests, type, parent) => {
    if (!tests || tests.length === 0) {
      return <p>No {type} test results available for {parent}.</p>;
    }
    const isBodyScores = type === 'Body Scores';

    return (
      <div className="health-tests-container">
        <h5>{parent} {type}</h5>
        <table className="health-tests-table">
          <thead>
            <tr>
              <th>{isBodyScores ? 'Category' : 'Disease Name'}</th>
              <th>{isBodyScores ? 'Score' : 'Result'}</th>
              {!isBodyScores && <th>Date Tested</th>}
            </tr>
          </thead>
          <tbody>
            {tests
              .sort((a, b) => a.testName.localeCompare(b.testName))
              .map((test, idx) => (
                <tr key={idx}>
                  <td>{test.testName.replace(isBodyScores ? /^Body Scores\s*-\s*/i : /^BBP\s*-\s*/i, '')}</td>
                  <td>{test.score || test.result}</td>
                  {!isBodyScores && <td>{test.dateTested ? new Date(test.dateTested).toLocaleDateString() : 'N/A'}</td>}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (isLoading) return <p>Loading litter...</p>;
  if (!litter) return <p>Litter not found.</p>;

  const currentPlayerId = playerId;
  const isBreeder = currentPlayerId === litter.playerId;
  const ownedPuppies = litter.puppies.filter(puppy => puppy.owner === currentPlayerId);
  const displayPuppies = isBreeder ? litter.puppies : ownedPuppies;

  return (
    <div className="litter-profile-container">
      <h1>Litter Profile</h1>
      {message && (
        <p className={message.includes('Error') ? 'error-message' : 'success-message'}>
          {message}
        </p>
      )}
      <div className="litter-parents">
        <div className="parent-image">
          <Link to={`/dog/${litter.sireId}`}>
            <img
              src={sireImageUrl}
              alt="Sire"
              onError={() => handleParentImageError('sire')}
            />
          </Link>
          <p>Sire: {dogLookup[litter.sireId]?.registeredName || litter.sireId}</p>
        </div>
        <span className="parent-divider">X</span>
        <div className="parent-image">
          <Link to={`/dog/${litter.damId}`}>
            <img
              src={damImageUrl}
              alt="Dam"
              onError={() => handleParentImageError('dam')}
            />
          </Link>
          <p>Dam: {dogLookup[litter.damId]?.registeredName || litter.damId}</p>
        </div>
      </div>
      <p><strong>Bred By:</strong> {kennelLookup[litter.bredBy] || 'Unknown'}</p>
      {litter.coBreederKennelId && (
        <p><strong>Co-Breeder:</strong> {kennelLookup[litter.coBreederKennelId] || 'Unknown'}</p>
      )}
      <p><strong>Status:</strong> {litter.status}</p>
      {litter.artUrl ? (
        <div>
          <img src={litter.artUrl} alt="Litter Art" className="litter-art" />
          {isBreeder && litter.status === 'Rolled' && (
            <button className="action-button remove-button" onClick={clearLitterArt}>
              Clear Art
            </button>
          )}
        </div>
      ) : (
        isBreeder && litter.status === 'Rolled' && (
          <div className="art-upload">
            <input
              className="text-input"
              value={artUrl}
              onChange={e => setArtUrl(e.target.value)}
              placeholder="Add Litter Artwork URL"
            />
            <button className="action-button" onClick={addArtUrl}>Save Art</button>
          </div>
        )
      )}
      {litter.status === 'Pending' && isBreeder && (
        <div className="co-breeder-status">
          {litter.coBreederKennelId ? (
            <p><strong>Co-Breeder Approval:</strong> {litter.coBreederApprovalStatus}</p>
          ) : (
            <p>No co-breeder selected.</p>
          )}
        </div>
      )}
      {litter.status === 'Pending' ? (
        <p>{litter.puppies.length} puppies pending roll</p>
      ) : (
        <div className="puppies-container">
          {litter.optOutHealthTests && (
            <p className="text-yellow-600 text-sm mb-2">
              This litter has opted out of DFG inherited disease testing.
            </p>
          )}
          <h3>{isBreeder ? 'Puppies' : 'Your Puppy'}</h3>
          {displayPuppies.length === 0 ? (
            <p>No puppies to display.</p>
          ) : (
            displayPuppies.map((puppy, idx) => {
              const effectiveStatus = puppy.status || 'Available';
              const isOwner = currentPlayerId === puppy.owner;
              console.log(`Puppy ${idx}:`, {
                puppyId: puppy.puppyId,
                status: effectiveStatus,
                ownerId: puppy.owner,
                breederId: litter.playerId,
                isOwner,
                isBreeder,
                isRegistered: puppy.isRegistered,
                ownerUsername: puppy.ownerUsername,
                registeredName: puppy.registeredName,
                artUrl: puppy.artUrl,
                displayUrl: puppyImageUrls[idx]
              });
              return (
                <div key={idx} className={`puppy-container ${puppy.gender === 'Female' ? 'female' : 'male'}`}>
                  <div className="puppy-image">
                    <img
                      src={puppyImageUrls[idx]}
                      alt={`Puppy ${idx + 1}`}
                      onError={() => handleImageError(idx)}
                    />
                  </div>
                  <div className="puppy-name">
                    {puppy.isRegistered ? (
                      <Link to={`/dog/${puppy.dogId?._id}`}>
                        {puppy.registeredName || puppy.name || `Puppy ${idx + 1}`} ({puppy.breed || 'Unknown'}, {puppy.color ? `${puppy.color} ${puppy.gender}` : puppy.gender || 'Unknown'})
                      </Link>
                    ) : effectiveStatus === 'Transferred' && !isOwner ? (
                      <span>
                        {puppy.name || `Puppy ${idx + 1}`} ({puppy.breed || 'Unknown'}, {puppy.color ? `${puppy.color} ${puppy.gender}` : puppy.gender || 'Unknown'})
                      </span>
                    ) : (
                      <span>
                        {puppy.name || `Puppy ${idx + 1}`} ({puppy.breed || 'Unknown'}, {puppy.color ? `${puppy.color} ${puppy.gender}` : puppy.gender || 'Unknown'})
                        {effectiveStatus === 'Retained' && (
                          <span className="breeder-pick-badge">Breeder’s Pick</span>
                        )}
                      </span>
                    )}
                  </div>
                  <p className="puppy-status">
                    Status: {effectiveStatus === 'Transferred' ? `Transferred to ${puppy.ownerUsername || 'Unknown'}` : effectiveStatus}
                  </p>
                  {editingPuppyIdx === idx && isBreeder ? (
                    <div className="puppy-details-editable">
                      <div className="detail-row">
                        <span className="detail-label">Name:</span>
                        <input
                          className="text-input"
                          value={puppyEdits.name}
                          onChange={e => setPuppyEdits({ ...puppyEdits, name: e.target.value })}
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Registered Name:</span>
                        <input
                          className="text-input"
                          value={puppyEdits.registeredName}
                          onChange={e => setPuppyEdits({ ...puppyEdits, registeredName: e.target.value })}
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Personality:</span>
                        <input
                          className="text-input"
                          value={puppyEdits.personality}
                          onChange={e => setPuppyEdits({ ...puppyEdits, personality: e.target.value })}
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Phenotype:</span>
                        <input
                          className="text-input"
                          value={puppyEdits.phenotype}
                          onChange={e => setPuppyEdits({ ...puppyEdits, phenotype: e.target.value })}
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Genotype:</span>
                        <input
                          className="text-input"
                          value={puppyEdits.genotype}
                          onChange={e => setPuppyEdits({ ...puppyEdits, genotype: e.target.value })}
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Collar Color:</span>
                        <input
                          className="text-input"
                          value={puppyEdits.color}
                          onChange={e => setPuppyEdits({ ...puppyEdits, color: e.target.value })}
                        />
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Image URL:</span>
                        <input
                          className="text-input"
                          value={puppyEdits.artUrl}
                          onChange={e => setPuppyEdits({ ...puppyEdits, artUrl: e.target.value })}
                          placeholder="Enter puppy image URL"
                        />
                      </div>
                      <div className="puppy-actions">
                        <button className="action-button" onClick={savePuppyEdits}>Save</button>
                        <button className="action-button remove-button" onClick={cancelEditingPuppy}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="puppy-details-readonly">
                      <div className="detail-row">
                        <span className="detail-label">Registered Name:</span>
                        <span className="detail-value">{puppy.registeredName || 'Not set'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Personality:</span>
                        <span className="detail-value">{puppy.personality || 'Not set'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Phenotype:</span>
                        <span className="detail-value">{puppy.phenotype || 'Not set'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Genotype:</span>
                        <span className="detail-value">{puppy.genotype || 'Not set'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Collar Color:</span>
                        <span className="detail-value">{puppy.color || 'Not set'}</span>
                      </div>
                    </div>
                  )}
                  {!litter.optOutHealthTests && puppy.healthTestRecords?.length > 0 && (
                    <div className="health-tests-section">
                      <button
                        className="action-button"
                        onClick={() => toggleHealthTests(idx)}
                      >
                        {healthTestVisibility[idx] ? 'Hide Health Tests' : 'Show Health Tests'}
                      </button>
                      {healthTestVisibility[idx] && (
                        <>
                          {renderHealthTestsTable(
                            puppy.healthTestRecords.filter(test => test && !test.testName.toLowerCase().startsWith('body scores')),
                            'BBP Tests',
                            `Puppy ${idx + 1}`
                          )}
                          {renderHealthTestsTable(
                            puppy.healthTestRecords.filter(test => test && test.testName.toLowerCase().startsWith('body scores')),
                            'Body Scores',
                            `Puppy ${idx + 1}`
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {(isOwner || isBreeder) && (
                    <div className="puppy-actions">
                      <button
                        className="action-button"
                        onClick={() => navigate('/register-dog', {
                          state: { litterId, puppy, puppyIndex: idx, sireId: litter.sireId, damId: litter.damId, breederId: litter.playerId, optOutHealthTests: litter.optOutHealthTests }
                        })}
                        disabled={
                          !isOwner ||
                          !['Available', 'Retained'].includes(effectiveStatus)
                        }
                      >
                        Register
                      </button>
                      {isBreeder && (
                        <>
                          <button
                            className="action-button"
                            onClick={() => toggleTransfer(idx)}
                            disabled={effectiveStatus !== 'Available'}
                          >
                            {transferIdx === idx ? 'Cancel Transfer' : 'Transfer'}
                          </button>
                          <button
                            className="action-button"
                            onClick={() => startEditingPuppy(idx)}
                          >
                            Edit
                          </button>
                          {effectiveStatus === 'Retained' ? (
                            <button
                              className="action-button keeper"
                              onClick={() => unmarkPuppyKeeper(idx)}
                            >
                              Unmark Keeper
                            </button>
                          ) : (
                            <button
                              className="action-button keeper"
                              onClick={() => markPuppyKeeper(idx)}
                              disabled={effectiveStatus !== 'Available'}
                            >
                              Mark as Keeper
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {isBreeder && transferIdx === idx && (
                    <div className="transfer-section">
                      <label>Transfer to Player:</label>
                      <Autosuggest
                        suggestions={suggestions}
                        onSuggestionsFetchRequested={({ value }) => fetchSuggestions(value)}
                        onSuggestionsClearRequested={() => setSuggestions([])}
                        getSuggestionValue={suggestion => suggestion}
                        renderSuggestion={suggestion => <div>{suggestion}</div>}
                        inputProps={{
                          value: newOwnerUsername,
                          onChange: (e, { newValue }) => setNewOwnerUsername(newValue),
                          className: 'text-input',
                          placeholder: 'Enter player username'
                        }}
                      />
                      <div className="puppy-actions">
                        <button
                          className="action-button"
                          onClick={() => transferPuppy(idx)}
                          disabled={!newOwnerUsername}
                        >
                          Confirm Transfer
                        </button>
                        <button
                          className="action-button remove-button"
                          onClick={() => toggleTransfer(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      {isBreeder && (
        <button
          className="action-button remove-button"
          onClick={async () => {
            if (!window.confirm('Are you sure you want to delete this litter?')) return;
            try {
              const res = await fetch(`${API_BASE_URL}/delete-litter/${litterId}`, { method: 'DELETE' });
              if (!res.ok) throw new Error(await res.text());
              setMessage('Litter deleted successfully.');
              navigate('/my-litters');
            } catch (error) {
              setMessage('Error: ' + error.message);
            }
          }}
          disabled={litter.coBreederApprovalStatus === 'Pending'}
        >
          Delete Litter
        </button>
      )}
    </div>
  );
};

export default LitterProfile;